// src/components/loyalty/ProgramCard.js
import React from 'react';
import {
    TagIcon,
    CurrencyDollarIcon,
    ArrowPathIcon,
    UsersIcon,
    CheckCircleIcon,
    XCircleIcon,
    PencilIcon,
    PlayIcon,
    PauseIcon,
} from '@heroicons/react/24/outline';

const getProgramTypeIcon = (type) => {
    switch (type) {
        case 'Point-based': return <TagIcon className="h-6 w-6 text-indigo-500" />;
        case 'Tiered': return <ArrowPathIcon className="h-6 w-6 text-green-500" />;
        case 'Spend-based': return <CurrencyDollarIcon className="h-6 w-6 text-purple-500" />;
        default: return null;
    }
};

const ProgramCard = ({ program, onEdit, onDeactivate, onActivate }) => {
    const isActive = program.status === 'Active';

    return (
        <div className="bg-white rounded-lg shadow-md p-6 flex flex-col justify-between border-t-4"
             style={{ borderColor: isActive ? '#6366F1' : '#A78BFA' }}> {/* Dynamic border color */}
            <div>
                <div className="flex justify-between items-center mb-3">
                    <h3 className="text-xl font-bold text-gray-800">{program.name}</h3>
                    <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {program.status}
            </span>
                        {getProgramTypeIcon(program.type)}
                    </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">{program.description}</p>

                <div className="space-y-2 text-sm text-gray-700 mb-5">
                    <div className="flex items-center">
                        <UsersIcon className="h-5 w-5 text-gray-500 mr-2" />
                        <span>Active Members: <span className="font-semibold">{program.members.toLocaleString()}</span></span>
                    </div>
                    {program.type === 'Point-based' && (
                        <>
                            <div className="flex items-center">
                                <TagIcon className="h-5 w-5 text-gray-500 mr-2" />
                                <span>Points Issued: <span className="font-semibold">{program.pointsIssued?.toLocaleString() || 'N/A'}</span></span>
                            </div>
                            <div className="flex items-center">
                                <CheckCircleIcon className="h-5 w-5 text-gray-500 mr-2" />
                                <span>Rewards Redeemed: <span className="font-semibold">{program.rewardsRedeemed?.toLocaleString() || 'N/A'}</span></span>
                            </div>
                        </>
                    )}
                    {program.type === 'Tiered' && (
                        <div className="flex items-center">
                            <ArrowPathIcon className="h-5 w-5 text-gray-500 mr-2" />
                            <span>Tiers: <span className="font-semibold">{program.tiers?.join(', ') || 'N/A'}</span></span>
                        </div>
                    )}
                    {program.type === 'Spend-based' && (
                        <div className="flex items-center">
                            <CurrencyDollarIcon className="h-5 w-5 text-gray-500 mr-2" />
                            <span>Thresholds: <span className="font-semibold">{program.thresholds?.join(', ') || 'N/A'}</span></span>
                        </div>
                    )}
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                <button
                    onClick={() => onEdit(program.id)} // Pass program ID for editing
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-indigo-600 transition-colors duration-200"
                    title="Edit Program"
                >
                    <PencilIcon className="h-5 w-5" />
                </button>
                {isActive ? (
                    <button
                        onClick={() => onDeactivate(program.id)}
                        className="p-2 rounded-full text-red-500 hover:bg-red-100 hover:text-red-700 transition-colors duration-200"
                        title="Deactivate Program"
                    >
                        <PauseIcon className="h-5 w-5" />
                    </button>
                ) : (
                    <button
                        onClick={() => onActivate(program.id)}
                        className="p-2 rounded-full text-green-500 hover:bg-green-100 hover:text-green-700 transition-colors duration-200"
                        title="Activate Program"
                    >
                        <PlayIcon className="h-5 w-5" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default ProgramCard;